import React, { useState } from 'react'
import StatusChip from '../../components/ui/StatusChip'
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { useInventoryStock, useStockLedger } from '../../hooks/api/useInventory'
import { StockMovementType, StockReferenceType } from '../../types'
import StockIcon from '../../components/icons/StockIcon'
import LedgerIcon from '../../components/icons/LedgerIcon'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const TabPanel = ({ children, value, index }: TabPanelProps) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
)

const InventoryPage: React.FC = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState(0)

  // Stock tab state
  const [stockSearch, setStockSearch] = useState('')

  // Ledger tab state
  const [ledgerPage, setLedgerPage] = useState(0)
  const [ledgerLimit] = useState(50)
  const [movementFilter, setMovementFilter] = useState<StockMovementType | ''>('')
  const [refTypeFilter, setRefTypeFilter] = useState<StockReferenceType | ''>('')
  const [ledgerFrom, setLedgerFrom] = useState('')
  const [ledgerTo, setLedgerTo] = useState('')

  const {
    data: stocks,
    isLoading: stockLoading,
    isError: stockError,
    refetch: refetchStock,
  } = useInventoryStock({ search: stockSearch || undefined })

  const {
    data: ledgerData,
    isLoading: ledgerLoading,
    isError: ledgerError,
    refetch: refetchLedger,
  } = useStockLedger({
    page: ledgerPage + 1,
    limit: ledgerLimit,
    movement_type: movementFilter || undefined,
    reference_type: refTypeFilter || undefined,
    from_date: ledgerFrom || undefined,
    to_date: ledgerTo || undefined,
  })

  const stockList = [...(stocks ?? [])].sort((a, b) => {
    const orderA = a.sku_id ? (a.sku_id.sortOrder ?? 999999) : Number.MAX_SAFE_INTEGER
    const orderB = b.sku_id ? (b.sku_id.sortOrder ?? 999999) : Number.MAX_SAFE_INTEGER
    return orderA - orderB || a.description.localeCompare(b.description)
  })
  const outOfStock = stockList.filter((s) => s.current_stock_ctn <= 0 && s.current_stock_pcs <= 0)
  const ledgerEntries = ledgerData?.entries ?? []
  const ledgerTotal = ledgerData?.total ?? 0

  const refTypeLabel: Record<StockReferenceType, string> = {
    purchase_invoice: 'Purchase Invoice',
    load_form: 'Load Form',
  }

  const refPath = (entry: { reference_type: StockReferenceType; reference_id: string }) => {
    if (entry.reference_type === 'purchase_invoice') {
      return `/purchase-invoices/${entry.reference_id}`
    }
    return `/loadforms/${entry.reference_id}`
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Inventory
      </Typography>

      <Paper>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="fullWidth">
          <Tab icon={<StockIcon />} iconPosition="start" label="Current Stock" />
          <Tab icon={<LedgerIcon />} iconPosition="start" label="Stock Ledger" />
        </Tabs>

        {/* Current Stock Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ px: 2, pb: 2 }}>
            {/* Summary chips */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <StatusChip label={`Total SKUs tracked: ${stockList.length}`} variant="info" />
              <StatusChip
                label={`Out of stock: ${outOfStock.length}`}
                variant={outOfStock.length > 0 ? 'danger' : 'success'}
              />
            </Box>

            {/* Search & Refresh */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                label="Search description / SKU"
                value={stockSearch}
                onChange={(e) => setStockSearch(e.target.value)}
                sx={{ width: 300 }}
              />
              <Tooltip title="Refresh">
                <IconButton onClick={() => refetchStock()}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {stockError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to load inventory stock.
              </Alert>
            )}

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>SKU Name</TableCell>
                    <TableCell align="right">Stock CTN</TableCell>
                    <TableCell align="right">Stock PCS</TableCell>
                    <TableCell align="right">Last Purchase Rate</TableCell>
                    <TableCell>Last Updated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={32} />
                      </TableCell>
                    </TableRow>
                  ) : stockList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No stock records found. Create a purchase invoice to start tracking inventory.
                      </TableCell>
                    </TableRow>
                  ) : (
                    stockList.map((stock) => {
                      const isEmpty = stock.current_stock_ctn <= 0 && stock.current_stock_pcs <= 0
                      return (
                        <TableRow
                          key={stock._id}
                          sx={{ bgcolor: isEmpty ? 'error.50' : 'success.50' }}
                        >
                          <TableCell>
                            {stock.sku_id ? (
                              <Typography variant="body2" fontWeight={500}>
                                {stock.sku_id.name}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color={isEmpty ? 'error.main' : 'success.dark'}
                            >
                              {stock.current_stock_ctn.toFixed(0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color={isEmpty ? 'error.main' : 'success.dark'}
                            >
                              {stock.current_stock_pcs.toFixed(0)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {stock.last_purchase_rate.toLocaleString('en-PK', {
                              style: 'currency',
                              currency: 'PKR',
                              maximumFractionDigits: 2,
                            })}
                          </TableCell>
                          <TableCell>
                            {stock.last_updated
                              ? format(new Date(stock.last_updated), 'dd MMM yyyy')
                              : '—'}
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </TabPanel>

        {/* Stock Ledger Tab */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ px: 2, pb: 2 }}>
            {/* Filters */}
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="From Date"
                  type="date"
                  value={ledgerFrom}
                  onChange={(e) => { setLedgerFrom(e.target.value); setLedgerPage(0) }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="To Date"
                  type="date"
                  value={ledgerTo}
                  onChange={(e) => { setLedgerTo(e.target.value); setLedgerPage(0) }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Movement</InputLabel>
                  <Select
                    value={movementFilter}
                    label="Movement"
                    onChange={(e) => { setMovementFilter(e.target.value as any); setLedgerPage(0) }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="in">IN</MenuItem>
                    <MenuItem value="out">OUT</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Reference Type</InputLabel>
                  <Select
                    value={refTypeFilter}
                    label="Reference Type"
                    onChange={(e) => { setRefTypeFilter(e.target.value as any); setLedgerPage(0) }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="purchase_invoice">Purchase Invoice</MenuItem>
                    <MenuItem value="load_form">Load Form</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={1}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setMovementFilter('')
                    setRefTypeFilter('')
                    setLedgerFrom('')
                    setLedgerTo('')
                    setLedgerPage(0)
                  }}
                >
                  Clear
                </Button>
              </Grid>
              <Grid item>
                <Tooltip title="Refresh">
                  <IconButton onClick={() => refetchLedger()}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>

            {ledgerError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to load stock ledger.
              </Alert>
            )}

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>SKU / Description</TableCell>
                    <TableCell>Movement</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell align="right">Qty CTN</TableCell>
                    <TableCell align="right">Qty PCS</TableCell>
                    <TableCell align="right">Balance CTN</TableCell>
                    <TableCell align="right">Balance PCS</TableCell>
                    <TableCell align="center">Link</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ledgerLoading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={32} />
                      </TableCell>
                    </TableRow>
                  ) : ledgerEntries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No ledger entries found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    ledgerEntries.map((entry) => (
                      <TableRow key={entry._id}>
                        <TableCell>
                          {format(new Date(entry.date), 'dd MMM yyyy')}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {typeof entry.sku_id === 'object' && entry.sku_id
                              ? entry.sku_id.name
                              : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {entry.description}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusChip
                            label={entry.movement_type === 'in' ? 'IN' : 'OUT'}
                            variant={entry.movement_type === 'in' ? 'success' : 'danger'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {refTypeLabel[entry.reference_type]}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {entry.reference_id}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{entry.quantity_ctn.toFixed(0)}</TableCell>
                        <TableCell align="right">{entry.quantity_pcs.toFixed(0)}</TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            color={entry.balance_ctn_after < 0 ? 'error' : 'inherit'}
                          >
                            {entry.balance_ctn_after.toFixed(0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            color={entry.balance_pcs_after < 0 ? 'error' : 'inherit'}
                          >
                            {entry.balance_pcs_after.toFixed(0)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Open reference">
                            <IconButton
                              size="small"
                              onClick={() => navigate(refPath(entry))}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={ledgerTotal}
              page={ledgerPage}
              rowsPerPage={ledgerLimit}
              onPageChange={(_, newPage) => setLedgerPage(newPage)}
              rowsPerPageOptions={[50]}
            />
          </Box>
        </TabPanel>
      </Paper>
    </Box>
  )
}

export default InventoryPage
